import { fetch } from '@tauri-apps/plugin-http';
import {
  listPendingChanges,
  markPendingChangeFailed,
  markPendingChangeSynced,
  replaceEntityId,
  type PendingChange,
} from './dbUtils';

let inFlight = false;

function toRequestInit(change: PendingChange): RequestInit {
  return {
    method: change.method,
    headers: change.headers || {},
    body: change.body ?? undefined,
  } as RequestInit;
}

/**
 * Attempts to sync queued offline mutations to the server.
 * - Non-throwing by default: returns counts + last error string.
 * - Uses tauri plugin-http directly to avoid recursion through wrapped fetch.
 */
export async function processPendingChanges(params?: {
  userId?: string | null;
  limit?: number;
}): Promise<{ attempted: number; synced: number; failed: number }> {
  if (inFlight) return { attempted: 0, synced: 0, failed: 0 };
  inFlight = true;

  try {
    const pending = await listPendingChanges({
      userId: typeof params?.userId !== 'undefined' ? params.userId : undefined,
      status: 'pending',
      limit: params?.limit ?? 50,
    });

    let attempted = 0;
    let synced = 0;
    let failed = 0;

    for (const change of pending) {
      attempted++;
      try {
        const res = await fetch(change.url, toRequestInit(change));
        if (res.ok) {
          await markPendingChangeSynced(change.id);

          // Replace temp ID with server-assigned ID in local storage
          if (
            change.action === 'add' &&
            change.entityId?.startsWith('temp-') &&
            change.entityType
          ) {
            try {
              const json = await res.json();
              const serverId = json?.result?.id;
              if (serverId && typeof serverId === 'string') {
                const tableName = `${change.entityType}s`;
                await replaceEntityId(
                  tableName,
                  change.entityId,
                  serverId,
                  change.userId || undefined
                );
              }
            } catch {
              // Best effort - the entity will be corrected on next refresh anyway
            }
          }

          synced++;
        } else {
          await markPendingChangeFailed(
            change.id,
            `HTTP ${res.status} ${res.statusText || ''}`.trim()
          );
          failed++;
        }
      } catch (e: any) {
        await markPendingChangeFailed(
          change.id,
          e?.message ? String(e.message) : 'Unknown error'
        );
        failed++;
      }
    }

    return { attempted, synced, failed };
  } finally {
    inFlight = false;
  }
}


